import { getLgwToken, setLgwToken } from './session'
import { touchRecentGame } from '@/composables/useRecentGames'

const BASE = ''

async function lgw (path, { method = 'GET', body, token, _retried } = {}) {
  const auth = token || getLgwToken() || 'lgw-demo'
  const res = await fetch(`${BASE}/lgw${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth}`
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  })
  const json = await res.json().catch(() => ({
    success: false,
    errorCode: res.status || 1,
    message: '网络异常',
    data: null
  }))

  // 过期/无效会话：清掉 localStorage，避免后续请求继续带坏 token
  const unauthorized =
    res.status === 401 ||
    Number(json?.errorCode) === 401 ||
    /会话无效|未登录|登录已过期|会话不存在/.test(String(json?.message || ''))
  if (unauthorized && !_retried) {
    const stored = getLgwToken()
    if (stored && stored === auth) setLgwToken('')
    if (auth !== 'lgw-demo') {
      return lgw(path, { method, body, token: 'lgw-demo', _retried: true })
    }
  }
  return json
}

/** 从投注请求体里尽量解析出 gameId（兼容单注 / 批量字段） */
function extractGameIds (body) {
  if (!body || typeof body !== 'object') return []
  const ids = []
  const push = (v) => {
    const n = Number(v)
    if (n > 0 && !ids.includes(n)) ids.push(n)
  }
  push(body.gameId ?? body.game_id)
  if (Array.isArray(body.orders)) {
    body.orders.forEach((o) => push(o?.gameId ?? o?.game_id))
  }
  if (Array.isArray(body.items)) {
    body.items.forEach((o) => push(o?.gameId ?? o?.game_id))
  }
  if (Array.isArray(body.list)) {
    body.list.forEach((o) => push(o?.gameId ?? o?.game_id))
  }
  return ids
}

function rememberRecentFromBet (body, res) {
  if (!res?.success) return
  const fromBody = extractGameIds(body)
  if (fromBody.length) {
    // 多个时按请求顺序依次 touch（最后一个会在最前，故倒序）
    ;[...fromBody].reverse().forEach((id) => touchRecentGame(id))
    return
  }
  const data = res.data
  if (data?.gameId) touchRecentGame(data.gameId)
  if (Array.isArray(data?.orders)) {
    ;[...data.orders].reverse().forEach((o) => {
      if (o?.gameId) touchRecentGame(o.gameId)
    })
  }
}

export const lgwApi = {
  exchangeSso: (ticket) => lgw('/sso/exchange', { method: 'POST', body: { ticket }, token: 'lgw-demo' }),
  session: (token) => lgw(`/sessions/${token || getLgwToken() || 'lgw-demo'}`),
  account: () => lgw('/accounts/'),
  playerSettings: () => lgw('/players/settings'),
  updatePlayerSettings: (body) => lgw('/players/settings', { method: 'PATCH', body }),
  gamesMobile: () => lgw('/games/mobile'),
  recommendation: () => lgw('/games/recommendation'),
  popularSettings: () => lgw('/games/popular_settings'),
  favorites: () => lgw('/games/favorite_game'),
  toggleFavorite: (gameId) => lgw('/games/favorite_game', { method: 'POST', body: { gameId } }),
  near: (gameId) => lgw(`/numeros/near?gameId=${gameId}`),
  draws: (gameId, size, opts = {}) => {
    const q = new URLSearchParams()
    if (size) q.set('size', String(size))
    if (opts.refresh) q.set('refresh', '1')
    const qs = q.toString()
    return lgw(`/draw/${gameId}${qs ? `?${qs}` : ''}`)
  },
  downloadDrawNumero: async (gameId) => {
    const auth = getLgwToken() || 'lgw-demo'
    const res = await fetch(`${BASE}/lgw/draw/download/numero/?gameId=${gameId}`, {
      headers: { Authorization: `Bearer ${auth}` }
    })
    if (!res.ok) throw new Error('download failed')
    return res.text()
  },
  twoSideMenu: (gameId) => lgw(`/two_side_play/group_menu/MOBILE/${gameId}`),
  twoSidePlays: () => lgw('/two_side_play/play_menu/MOBILE/'),
  bet: async (body) => {
    // 下单前刷新期号，避免快开彩换期导致「期号已过期」400
    let payload = body
    try {
      const gid = Number(body?.gameId || body?.game_id || 0)
      if (gid > 0) {
        const nearRes = await lgw(`/numeros/near?gameId=${gid}`)
        const cur = nearRes?.data?.current
        if (cur?.closed) {
          return { success: false, errorCode: 1, message: '已封盘，无法投注', data: null }
        }
        if (cur?.numero != null) {
          payload = { ...body, numero: String(cur.numero) }
        }
      }
    } catch (_) {
      /* 刷新失败时仍用原 body，由服务端再校验 */
    }
    const res = await lgw('/orders/betting', { method: 'POST', body: payload })
    rememberRecentFromBet(payload, res)
    return res
  },
  betTwoSide: async (body) => {
    let payload = body
    try {
      const gid = Number(body?.gameId || body?.game_id || 0)
      if (gid > 0) {
        const nearRes = await lgw(`/numeros/near?gameId=${gid}`)
        const cur = nearRes?.data?.current
        if (cur?.closed) {
          return { success: false, errorCode: 1, message: '已封盘，无法投注', data: null }
        }
        if (cur?.numero != null) {
          payload = { ...body, numero: String(cur.numero) }
        }
      }
    } catch (_) {}
    const res = await lgw('/orders/betting/two_side', { method: 'POST', body: payload })
    rememberRecentFromBet(payload, res)
    return res
  },
  chaseNumeros: (gameId, count = 10) => lgw(`/numeros/chase/?gameId=${gameId}&count=${count}`),
  getCart: () => lgw('/orders/cart'),
  addCart: (body) => lgw('/orders/cart', { method: 'POST', body }),
  updateCartItem: (id, body) => lgw(`/orders/cart/${id}`, { method: 'PATCH', body }),
  removeCartItem: (id) => lgw(`/orders/cart/${id}`, { method: 'DELETE' }),
  clearCart: () => lgw('/orders/cart', { method: 'DELETE' }),
  checkoutCart: async () => {
    const res = await lgw('/orders/cart/checkout', { method: 'POST' })
    rememberRecentFromBet({}, res)
    return res
  },
  orders: (query = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== '' && v !== 0))
    ).toString()
    return lgw(`/v2/orders${q ? `?${q}` : ''}`)
  },
  orderSum: (query = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== ''))
    ).toString()
    return lgw(`/v2/orders/sum${q ? `?${q}` : ''}`)
  },
  cancel: (id) => lgw('/orders/suborders/cancel', { method: 'POST', body: { id } })
}
